/**
 * TinyEditorComponent - A wrapper component for TinyMCE rich text editor
 *
 * PURPOSE:
 * This component provides a fully-featured WYSIWYG text editor with custom functionality
 * for message composition in the Relay portal. It enables users to format text, insert
 * personalization fields (mentions), and create rich content with tables, links, and lists.
 *
 * KEY FEATURES:
 * - Rich text formatting (bold, italic, underline, headings, font sizes, colors)
 * - Superscript/subscript support
 * - Personalization fields via @mentions autocomplete
 * - Custom menu buttons for input and account fields
 * - Table, link, and list insertion
 * - Optional code view for raw HTML editing
 * - Integration with Angular forms via ControlValueAccessor
 *
 * HOW IT WORKS:
 * 1. Implements ControlValueAccessor to work seamlessly with Angular Reactive Forms
 * 2. Initializes TinyMCE editor instance after view is rendered (ngAfterViewInit)
 * 3. Provides custom toolbar buttons and menu options based on configuration
 * 4. Handles bidirectional data binding between Angular forms and TinyMCE content
 * 5. Supports autocomplete suggestions when user types '@' followed by field names
 * 6. Cleans up editor instance on component destruction to prevent memory leaks
 */
import {
  AfterViewInit,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  NG_VALUE_ACCESSOR,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TinyEditorConfig } from '@app/core/services/tiny-editor.service';
import { LoggerService } from '@app/core/services/logger.service';
import tinymce from 'tinymce';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';
import 'tinymce/plugins/table';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/code';
import { registerRtlAutoDetectPlugin } from './rtl-auto-detect.plugin';

// Register the RTL auto-detect plugin once at module load time
registerRtlAutoDetectPlugin();

/**
 * Type definition for mention autocomplete entries
 * The mentions_fetch plugin requires entries with this shape to display
 * autocomplete suggestions when users type '@' in the editor.
 */
type MentionAutocompleteEntry = {
  id: string; // Unique identifier (required by mentions plugin but not used in our implementation)
  name: string; // Display name shown in autocomplete dropdown
};

@Component({
  selector: 'app-tiny-editor',
  templateUrl: './tiny-editor.component.html',
  styleUrls: ['./tiny-editor.component.scss'],
  providers: [
    {
      // Register this component as a ControlValueAccessor to enable two-way
      // data binding with Angular forms (formControl, ngModel)
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TinyEditorComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule],
})
export class TinyEditorComponent
  implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor
{
  /** Unique ID for the TinyMCE editor element (required for initialization) */
  @Input() elementId: string;

  /** Optional form control for integration with reactive forms */
  @Input() formControl: UntypedFormControl;

  /** Configuration object defining editor behavior, dimensions, and menu options */
  @Input() config?: TinyEditorConfig;

  /** Current editor content value */
  @Input('value') _value: string;

  /** Flag to enable/disable raw HTML code view button in toolbar */
  @Input() isRawEditorEnabled?: boolean = false;

  /** Reference to the TinyMCE editor instance for programmatic control */
  editorRef: any;

  constructor() {}

  /** Getter for the current editor value */
  get value() {
    return this._value;
  }

  /** Setter for the editor value, triggers internal setValue method */
  set value(val) {
    this.setValue(val);
  }

  /** Callback function called when editor content changes (part of ControlValueAccessor) */
  onChange: any = () => {};

  /** Callback function called when editor is touched/blurred (part of ControlValueAccessor) */
  onTouched: any = () => {};

  /**
   * Fetches available input fields for autocomplete suggestions
   *
   * This method is called by the mentions plugin when the user types '@'.
   * It converts the configured menu items into the format expected by the
   * mentions autocomplete plugin (MentionAutocompleteEntry).
   *
   * @returns Promise resolving to array of autocomplete entries
   */
  fetchInputFields = async () =>
    new Promise((resolve, _reject) => {
      let inputFields = [];
      const menuItems = this.config?.allMenuItems;
      if (menuItems) {
        inputFields = menuItems.map((item) => {
          return {
            id: item.name, // id field is not used by our code but required for the mentions plugin
            name: item.name,
          };
        });
      }
      return resolve(inputFields);
    });

  /**
   * Angular lifecycle hook - called once on component initialization
   * Ensures a default configuration exists if none was provided
   */
  ngOnInit() {
    if (this.config == null) {
      this.config = new TinyEditorConfig();
    }
  }

  /**
   * Angular lifecycle hook - called when component is destroyed
   * Properly cleans up the TinyMCE editor instance to prevent memory leaks
   */
  ngOnDestroy() {
    tinymce.remove(this.editorRef);
  }

  /**
   * Angular lifecycle hook - called after view is fully initialized
   *
   * Initializes the TinyMCE editor with comprehensive configuration including:
   * - Toolbar buttons and formatting options
   * - Custom plugins (table, lists, link, code, mentions)
   * - Mentions autocomplete for personalization fields
   * - Custom styling and branding
   * - Event handlers for content changes
   */
  async ngAfterViewInit() {
    await tinymce.init({
      license_key: 'gpl', // Using GPL license for TinyMCE
      selector: '#' + this.elementId, // Target element for editor initialization
      base_url: '/assets/tinymce', // Path to TinyMCE assets
      suffix: '.min', // Use minified TinyMCE files
      block_formats:
        'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4;',
      skin_url: '/assets/tinymce/skins/ui/oxide',
      min_height: this.config.minHeight,
      max_height: this.config.maxHeight,
      menu: {},
      menubar: false, // Hide default menubar
      plugins: [
        'table',
        'lists',
        'link',
        'code',
        'mentions',
        'rtl-auto-detect',
      ],
      extended_valid_elements: 'ul,li,ol,strong,u,em,b,br,i,strike', // Allow specific HTML elements
      entity_encoding: 'raw', // Don't encode HTML entities
      // Define two rows of toolbar buttons
      toolbar: [
        'blocks fontsizeinput forecolor | fontStyleBundle | superscriptSubscript',
        `bullist numlist align link table inputs account ${
          this.isRawEditorEnabled && 'code'
        }`,
      ],
      formats: {
        underline: { inline: 'u', exact: true }, // Use <u> tag for underline formatting
      },
      /**
       * Paste preprocessing – normalises pasted HTML before TinyMCE renders it:
       * 1. Converts <pre> wrappers to <p> so content isn't locked into
       *    monospace/preformatted structure (the root cause of the nested
       *    `div > pre > span` that breaks per-span direction detection).
       * 2. Strips inline font-family and font-size so pasted Arabic or mixed
       *    content inherits the editor's default typography.
       */
      paste_preprocess: (_plugin: any, args: { content: string }) => {
        // Step 1: unwrap <pre> blocks → <p> to normalise paste structure
        args.content = args.content
          .replace(/<pre(\s[^>]*)?>/gi, '<p$1>')
          .replace(/<\/pre>/gi, '</p>');

        // Step 2: strip font-family and font-size from inline styles
        args.content = args.content.replace(
          /(<[a-zA-Z][^>]*?)\sstyle="([^"]*)"/gi,
          (_match: string, tag: string, style: string) => {
            const cleaned = style
              .replace(/font-family\s*:[^;]+;?/gi, '')
              .replace(/font-size\s*:[^;]+;?/gi, '')
              .trim()
              .replace(/;$/, '');
            return cleaned ? `${tag} style="${cleaned}"` : tag;
          },
        );
      },

      contextmenu: false, // Disable right-click context menu
      browser_spellcheck: this.config.spellCheck,
      statusbar: this.config.showStatusBar,
      resize: this.config.allowResize,
      content_css: '/assets/tinymce/relay-editor-overrides.css', // Custom CSS for editor content
      branding: false, // Hide TinyMCE branding
      relative_urls: false, // Use absolute URLs for links

      // Mentions plugin configuration for @field autocomplete
      mentions_min_chars: 0, // Show suggestions immediately after typing '@'
      mentions_selector: 'span.relay_mention', // CSS selector for mention elements (must match mentions_menu_complete output)

      /**
       * Mentions fetch callback - retrieves and filters autocomplete suggestions
       * Called when user types '@' in the editor. Filters results based on user input.
       */
      mentions_fetch: (query, success) => {
        this.fetchInputFields().then((fields: MentionAutocompleteEntry[]) => {
          // Filter fields to only show matches for what the user has typed
          fields = fields.filter((field) => {
            return field.name.indexOf(query.term.toLowerCase()) !== -1;
          });
          success(fields);
        });
      },

      /**
       * Mentions menu complete callback - creates DOM element when user selects a suggestion
       * Called when user selects an autocomplete option (click or Enter key)
       * Returns a styled span element with the mention text
       */
      mentions_menu_complete: (editor, inputField) => {
        const span = editor.getDoc().createElement('span');
        span.className = 'relay_mention'; // Apply custom styling for mentions
        span.appendChild(
          editor.getDoc().createTextNode(`@{${inputField.name}}`),
        );
        return span;
      },

      // Callback executed after editor instance is created (for UI cleanup)
      init_instance_callback: this.init_instance_callback,

      /**
       * Setup callback - called during editor initialization
       * Used to store editor reference and configure custom buttons/events
       */
      setup: (editor) => {
        this.editorRef = editor;
        this.setupEditorInstance(editor, this.editorRef);
      },
    });
  }

  /**
   * Callback method called after the editor has been initialized.
   * Needed to remove the status bar text shown by default.
   *
   * @param mce - the editor instance object reference
   */
  init_instance_callback(mce: any) {
    try {
      const divEl = mce
        .getContainer()
        .getElementsByClassName(
          'mce-branding mce-widget mce-label mce-flow-layout-item mce-last',
        );
      if (divEl?.length === 1) {
        divEl[0].style.display = 'none';
      }
      const spanEl = mce
        .getContainer()
        .getElementsByClassName('mce-path mce-flow-layout-item mce-first');
      if (spanEl?.length === 1) {
        spanEl[0].style.display = 'none';
      }
    } catch (e) {
      LoggerService.log(
        'tiny-editor.component',
        'tiny editor was not initialized properly',
      );
    }
  }

  /**
   * Configures the TinyMCE editor instance with custom functionality
   *
   * Sets up:
   * - Event listeners for content changes
   * - Custom superscript/subscript split button
   * - Optional personalization menu buttons (inputs and account fields)
   *
   * @param editor - TinyMCE editor instance
   * @param editorRef - Reference to the editor for later use
   */
  setupEditorInstance(editor, editorRef) {
    // Listen to multiple events to detect content changes and trigger form updates
    editor.on('keyup change blur', () => {
      const content = editor.getContent();
      this.onEditorContentChange(content);
    });

    // Track current mode for superscript/subscript button (starts as Superscript)
    let buttonMode = 'Superscript';

    /**
     * Register custom split button for superscript/subscript formatting
     *
     * This button allows users to toggle between superscript and subscript modes.
     * Clicking the button applies the current mode, and the dropdown allows switching modes.
     * The button adapts its icon and state based on the current selection.
     */
    editor.ui.registry.addSplitButton('superscriptSubscript', {
      icon: 'superscript',
      // Called when main button is clicked - applies current mode (superscript or subscript)
      onAction: function (api) {
        editor.execCommand(buttonMode);
        api.setActive(!api.isActive());
      },
      // Called when dropdown item is selected - switches between superscript/subscript
      onItemAction: function (api, value) {
        if (api.isActive()) {
          editor.execCommand(buttonMode); // Remove current formatting first
          editor.execCommand(value); // Apply new formatting
        }
        buttonMode = value;
        api.setIcon(value);
      },
      // Populate dropdown menu with the alternate option (if in superscript, show subscript option)
      fetch: (resolve) => {
        resolve([
          buttonMode === 'Subscript'
            ? { type: 'choiceitem', icon: 'superscript', value: 'Superscript' }
            : { type: 'choiceitem', icon: 'subscript', value: 'Subscript' },
        ]);
      },
      // Setup event listener to update button state when selection changes
      onSetup: function (api) {
        editor.on('SelectionChange', () => {
          const nodeName = editor.selection.getNode().nodeName;
          if (nodeName === 'SUP') {
            buttonMode = 'Superscript';
            api.setIcon('Superscript');
            api.setActive(true);
          } else if (nodeName === 'SUB') {
            buttonMode = 'Subscript';
            api.setIcon('Subscript');
            api.setActive(true);
          } else if (api.isActive()) {
            api.setActive(false);
          }
        });
        // Cleanup function to remove event listener when button is destroyed
        return (_api) => {
          editor.off('SelectionChange');
        };
      },
    });

    // Font style formats shown on the split button, in icon-priority order
    // (used to pick which active format to surface when more than one applies)
    const fontStyleIcons: Record<string, string> = {
      Bold: 'bold',
      Italic: 'italic',
      Strikethrough: 'strike-through',
      Underline: 'underline',
    };
    const fontStyleFormats = Object.keys(fontStyleIcons);

    // Track current mode for the font style button (starts as Bold)
    let fontStyleMode = 'Bold';

    /**
     * Register custom split button for font style formatting
     *
     * Unlike superscript/subscript, these formats aren't mutually exclusive
     * (text can be bold, italic, and underlined at once). The main button
     * always toggles whichever format it currently represents, the dropdown
     * lets the user switch which format that is, and the icon/active state
     * follows whatever format is active at the selection.
     */
    editor.ui.registry.addSplitButton('fontStyleBundle', {
      icon: fontStyleIcons[fontStyleMode],
      // Called when main button is clicked - toggles the current mode's format
      onAction: function (api: any) {
        editor.execCommand(fontStyleMode);
        api.setActive(!api.isActive());
      },
      // Called when a dropdown item is selected - switches mode and ensures
      // that format is applied (does not remove it if already active, since
      // these formats can coexist and removal is the main button's job)
      onItemAction: function (api: any, value: string) {
        fontStyleMode = value;
        api.setIcon(fontStyleIcons[value]);
        if (!editor.formatter.match(value.toLowerCase())) {
          editor.execCommand(value);
        }
      },
      // Shows a checkmark on dropdown items whose format is active at the selection
      select: (value: string) => editor.formatter.match(value.toLowerCase()),
      // Populate dropdown with the font style options other than the current mode
      fetch: (resolve: (items: any[]) => void) => {
        resolve(
          fontStyleFormats
            .filter((format) => format !== fontStyleMode)
            .map((format) => ({
              type: 'choiceitem',
              icon: fontStyleIcons[format],
              text: format,
              value: format,
            })),
        );
      },
      // Setup event listener to update button icon/state when selection changes
      onSetup: function (api: any) {
        const updateState = () => {
          const matchedFormat = fontStyleFormats.find((format) =>
            editor.formatter.match(format.toLowerCase()),
          );
          if (matchedFormat) {
            fontStyleMode = matchedFormat;
            api.setIcon(fontStyleIcons[matchedFormat]);
            api.setActive(true);
          } else if (api.isActive()) {
            api.setActive(false);
          }
        };
        editor.on('NodeChange', updateState);
        return (_api) => {
          editor.off('NodeChange', updateState);
        };
      },
    });

    /**
     * Register custom menu button for input field personalization
     *
     * If enabled in config, adds a toolbar button that displays a dropdown menu
     * of available input fields. Clicking an item inserts the field mention into the editor.
     */
    if (this.config.useInputsMenuList) {
      const inputsMenuList = [...this.config.inputsMenuList];
      editor.ui.registry.addMenuButton('inputs', {
        text: '@{input_}',
        tooltip: 'Personalization Options',
        fetch: function (callback) {
          // Convert input fields into menu items
          const items = inputsMenuList.map((c) => {
            return {
              type: 'menuitem',
              text: c.name,
              value: `@{${c.name}} `,
              onAction: function () {
                // Insert mention as a styled span element
                editor.insertContent(
                  `<span class="relay_mention" data-mce-mentions-id="${c.name}">@{${c.name}}</span>`,
                );
              },
            };
          });
          callback(items);
        },
      });
    }

    /**
     * Register custom menu button for account field personalization
     *
     * If enabled in config, adds a toolbar button that displays a dropdown menu
     * of available account fields. Similar to the inputs button but for account-related data.
     */
    if (this.config.useAccountMenuList) {
      const accountMenuList = [...this.config.accountMenuList];
      editor.ui.registry.addMenuButton('account', {
        text: '@{account_}',
        tooltip: 'Account Personalization Options',
        fetch: function (callback) {
          // Convert account fields into menu items
          const items = accountMenuList.map((c) => {
            return {
              type: 'menuitem',
              text: c.name,
              value: `@{${c.name}} `,
              onAction: function () {
                // Insert mention as a styled span element
                editor.insertContent(
                  `<span class="relay_mention" data-mce-mentions-id="${c.name}">@{${c.name}}</span>`,
                );
              },
            };
          });
          callback(items);
        },
      });
    }
  }

  /**
   * Handles editor content changes and propagates them to Angular forms
   *
   * Called whenever the editor content changes (keyup, change, blur events).
   * Updates internal value and notifies Angular forms of the change.
   *
   * @param event - The new HTML content from the editor
   */
  onEditorContentChange(event: string) {
    if (event !== this.value) {
      this.setValue(event);
      this.onChange(event); // Notify Angular forms of change
      this.onTouched(); // Mark control as touched
    }
  }

  /**
   * Internal method to update the component's value
   * Handles undefined values by converting them to null for proper display
   *
   * @param val - The new value to set
   */
  setValue(val) {
    // textarea needs value set to null to show ''; 'undefined' will show if value is undefined
    if (val === undefined) {
      this._value = null;
    } else {
      this._value = val;
    }
  }

  /**
   * ControlValueAccessor interface method - writes a new value to the component
   *
   * Called by Angular forms when the form control value changes programmatically.
   * Updates both the internal value and the TinyMCE editor content.
   *
   * @param val - The new value from the form control
   */
  writeValue(val) {
    this.value = val;
    if (this.editorRef && val !== undefined && val !== null) {
      const currentContent = this.editorRef.getContent();
      if (currentContent !== val) {
        this.editorRef.setContent(val); // Tell TinyMCE to update its content
      }
    }
  }

  /**
   * ControlValueAccessor interface method - registers callback for value changes
   * Angular forms uses this to be notified when the editor content changes
   *
   * @param fn - Callback function to invoke on value change
   */
  registerOnChange(fn) {
    this.onChange = fn;
  }

  /**
   * ControlValueAccessor interface method - registers callback for touch events
   * Angular forms uses this to mark the control as touched (for validation)
   *
   * @param fn - Callback function to invoke when control is touched
   */
  registerOnTouched(fn) {
    this.onTouched = fn;
  }
}
